/**
 * Selection API Tools
 *
 * Defines client-side agentic tools that wrap the CefSharp-bound SelectionApi.
 * Each tool maps to a method on the C# SelectionApi class exposed via CefSharp.
 *
 * CefSharp specifics:
 *   - Methods are camelCase on the JS side
 *   - All methods return Promise<string> (JSON-encoded)
 *   - Arguments must be passed positionally (not as an object)
 */

import { toToolDisplayName, type RuntimeToolDef } from "./runtimeTypes";
import { getAPI } from "../services/workspaceService";
import { invokeCefSharpMethod } from "../services/cefSharpBinding";

const TIMEOUT_MS = 30_000;

// ---------------------------------------------------------------------------
// Tool specifications
// ---------------------------------------------------------------------------

interface SelectionApiToolSpec {
  /** Method name on the JS-side selectionApi object (camelCase). */
  method: string;
  /** Tool name exposed to the agent. */
  name: string;
  description: string;
  parameters: RuntimeToolDef["definition"]["parameters"];
  /** Ordered argument names matching the C# method signature. */
  argOrder: string[];
}

const TOOL_SPECS: SelectionApiToolSpec[] = [
  // ── Read — Current selection ──────────────────────────────────────────
  {
    method: "getCurrentSelection",
    name: "WS-selectionApi-getCurrentSelection",
    description:
      "Returns a JSON object with the current selection state. " +
      "Contains count and a list of selected features with featureId, guid, name, featureTypeId, and featureTypeName.",
    parameters: { type: "object", properties: {}, required: [] },
    argOrder: [],
  },

  // ── Write — Clear selection ───────────────────────────────────────────
  {
    method: "clearSelection",
    name: "WS-selectionApi-clearSelection",
    description:
      'Clears the current selection in the Quadri model. Returns "true" on success, "false" on failure.',
    parameters: { type: "object", properties: {}, required: [] },
    argOrder: [],
  },

  // ── Write — Select features ───────────────────────────────────────────
  {
    method: "selectFeatures",
    name: "WS-selectionApi-selectFeatures",
    description:
      "Selects features by their numeric IDs. Replaces the current selection. " +
      "featureIdsJson must be a JSON array of integer feature IDs, e.g. '[101, 203, 305]'. " +
      "Returns the number of features actually selected.",
    parameters: {
      type: "object",
      properties: {
        featureIdsJson: {
          type: "string",
          description:
            'JSON array of integer feature IDs to select, e.g. "[101, 203, 305]".',
        },
      },
      required: ["featureIdsJson"],
    },
    argOrder: ["featureIdsJson"],
  },
];

// ---------------------------------------------------------------------------
// CefSharp-aware callback factory
// ---------------------------------------------------------------------------

function createCefSharpCallback(
  method: string,
  argOrder: string[],
): (args: Record<string, unknown>) => Promise<string> {
  return async (args: Record<string, unknown>): Promise<string> => {
    try {
      const api = getAPI();
      const selectionApi = api?.selectionApi;
      if (!selectionApi) {
        return JSON.stringify({
          success: false,
          error:
            "SelectionApi not bound — CefSharp.BindObjectAsync may not have completed.",
        });
      }

      const fn = selectionApi[method];
      if (typeof fn !== "function") {
        return JSON.stringify({
          success: false,
          error: `Unknown method: selectionApi.${method}`,
        });
      }

      const positionalArgs = argOrder.map((name) => args?.[name]);
      const raw = await invokeCefSharpMethod<string>(
        "selectionApi",
        selectionApi,
        method,
        positionalArgs,
      );

      let data: unknown;
      try {
        data = JSON.parse(raw);
      } catch {
        data = raw;
      }

      return JSON.stringify({ success: true, data });
    } catch (error) {
      return JSON.stringify({
        success: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create RuntimeToolDef entries for every SelectionApi method.
 * Call this after `CefSharp.BindObjectAsync("selectionApi")` has resolved.
 */
export function createSelectionApiTools(): Record<string, RuntimeToolDef> {
  const tools: Record<string, RuntimeToolDef> = {};

  for (const spec of TOOL_SPECS) {
    tools[spec.name] = {
      definition: {
        name: spec.name,
        description: spec.description,
        deferred: true,
        title: toToolDisplayName(spec.name),
        parameters: spec.parameters,
      },
      callback: createCefSharpCallback(spec.method, spec.argOrder),
      timeOutInMs: TIMEOUT_MS,
    };
  }

  return tools;
}
